import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Trash2, Minus, Plus, ShoppingCart, Banknote, ScrollText } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useToast } from "../hooks/use-toast";
import { api } from "../services/api";

const fmt = (n) => new Intl.NumberFormat("fr-DZ").format(Math.round(n));

const CartDrawer = () => {
  const { t, tf, cartOpen, setCartOpen, cartDetailed, updateQty, removeFromCart, totalHT, totalTVA, totalTTC, clearCart } = useApp();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [pay, setPay] = useState("");
  const [chq, setChq] = useState({ no: "", bank: "", date: "" });

  const checkout = async () => {
    if (!pay) {
      toast({ title: "Ssuq", description: tf({ fr: "Choisissez un mode de paiement (espèces ou chèque).", ar: "اختر طريقة الدفع (نقداً أو شيك)." }) });
      return;
    }
    if (pay === "cheque" && (!chq.no || !chq.date)) {
      toast({ title: "Ssuq", description: tf({ fr: "Renseignez le n° de chèque et la date d'encaissement.", ar: "أدخل رقم الشيك وتاريخ الصرف." }) });
      return;
    }
    const paymentMethod = pay === "cheque" ? `Chèque — n° ${chq.no}, ${chq.bank || "banque ?"}, encaissement ${chq.date}` : "especes";
    try {
      let customer = {};
      try { customer = JSON.parse(sessionStorage.getItem("ssuq_client")) || {}; } catch { customer = {}; }
      await api.createOrder(cartDetailed.map((i) => ({ productId: i.id, qty: i.qty })), customer, paymentMethod);
      toast({ title: "Ssuq", description: tf({ fr: "Commande envoyée — bons de commande transmis aux fournisseurs (livraison sous 48h).", ar: "تم إرسال الطلب — أُرسلت سندات الطلب إلى الموردين (التسليم خلال 48 ساعة)." }) });
      clearCart();
      setPay(""); setChq({ no: "", bank: "", date: "" });
      setCartOpen(false);
    } catch {
      toast({ title: "Ssuq", description: tf({ fr: "Erreur lors de la commande. Réessayez.", ar: "خطأ أثناء الطلب. حاول مجدداً." }) });
    }
  };

  return (
    <>
      <div className={`fixed inset-0 z-[60] bg-navy-900/50 backdrop-blur-sm transition-opacity duration-300 ${cartOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setCartOpen(false)} />

      <aside className={`fixed top-0 z-[70] h-full w-full max-w-md bg-white shadow-2xl transition-transform duration-300 flex flex-col ltr:right-0 rtl:left-0 ${cartOpen ? "translate-x-0" : "ltr:translate-x-full rtl:-translate-x-full"}`}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="font-display text-xl font-extrabold text-navy flex items-center gap-2"><ShoppingCart size={20} className="text-orange" /> {t("cart_title")}</h2>
          <button onClick={() => setCartOpen(false)} className="p-2 text-navy hover:text-orange" aria-label="Fermer"><X size={22} /></button>
        </div>

        {cartDetailed.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <div className="w-16 h-16 rounded-full bg-cream flex items-center justify-center mb-4"><ShoppingCart size={28} className="text-orange" /></div>
            <p className="font-display font-bold text-navy">{t("cart_empty")}</p>
            <p className="text-sm text-gray-500 mt-1">{t("cart_empty_sub")}</p>
            <button onClick={() => { setCartOpen(false); navigate("/catalogue"); }} className="btn-orange font-display font-semibold rounded-full px-6 py-2.5 mt-6">{t("nav_catalogue")}</button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {cartDetailed.map((item) => (
                <div key={item.id} className="flex gap-3 border-b border-gray-100 pb-4">
                  <img src={item.image} alt={tf(item.name)} className="w-20 h-20 rounded-xl object-cover bg-cream shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-display font-semibold text-navy text-sm leading-snug line-clamp-2">{tf(item.name)}</h4>
                    {item.freeQty > 0 && (
                      <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-display font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full" data-testid={`cart-free-${item.id}`}>🎁 {item.freeQty} offert{item.freeQty > 1 ? "s" : ""}</span>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-gray-200 rounded-full">
                        <button onClick={() => updateQty(item.id, item.qty - 1)} className="p-1.5 text-navy" aria-label="-"><Minus size={13} /></button>
                        <span className="w-7 text-center font-display font-bold text-navy text-xs">{item.qty}</span>
                        <button onClick={() => updateQty(item.id, item.qty + 1)} className="p-1.5 text-navy" aria-label="+"><Plus size={13} /></button>
                      </div>
                      <span className="font-display font-bold text-navy text-sm">{fmt(item.lineHT)} {t("currency")}</span>
                    </div>
                  </div>
                  <button onClick={() => removeFromCart(item.id)} className="text-gray-300 hover:text-red-500 transition-colors self-start" aria-label={t("remove")}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 px-6 py-5 space-y-2">
              <div className="flex justify-between text-sm text-gray-500"><span>{t("cart_total_ht")}</span><span className="font-semibold text-navy">{fmt(totalHT)} {t("currency")}</span></div>
              <div className="flex justify-between text-sm text-gray-500"><span>{t("cart_tva")}</span><span className="font-semibold text-navy">{fmt(totalTVA)} {t("currency")}</span></div>
              <div className="flex justify-between font-display text-lg font-extrabold text-navy pt-2 border-t border-gray-100"><span>{t("cart_total_ttc")}</span><span>{fmt(totalTTC)} {t("currency")}</span></div>

              <div className="pt-2">
                <p className="font-display font-semibold text-gray-500 text-xs uppercase tracking-wider mb-2">{tf({ fr: "Mode de paiement", ar: "طريقة الدفع" })}</p>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setPay("especes")} data-testid="pay-especes" className={`flex items-center justify-center gap-2 rounded-xl border py-2.5 font-display font-semibold text-sm transition-colors ${pay === "especes" ? "bg-navy text-white border-navy" : "bg-white text-navy border-gray-200 hover:border-orange"}`}><Banknote size={16} /> {tf({ fr: "Espèces", ar: "نقداً" })}</button>
                  <button type="button" onClick={() => setPay("cheque")} data-testid="pay-cheque" className={`flex items-center justify-center gap-2 rounded-xl border py-2.5 font-display font-semibold text-sm transition-colors ${pay === "cheque" ? "bg-navy text-white border-navy" : "bg-white text-navy border-gray-200 hover:border-orange"}`}><ScrollText size={16} /> {tf({ fr: "Chèque", ar: "شيك" })}</button>
                </div>
                {pay === "cheque" && (
                  <div className="mt-2 grid grid-cols-1 gap-2" data-testid="cheque-details">
                    <input value={chq.no} onChange={(e) => setChq({ ...chq, no: e.target.value })} placeholder={tf({ fr: "N° de chèque", ar: "رقم الشيك" })} data-testid="cheque-no" className="px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-orange" />
                    <input value={chq.bank} onChange={(e) => setChq({ ...chq, bank: e.target.value })} placeholder={tf({ fr: "Banque", ar: "البنك" })} data-testid="cheque-bank" className="px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-orange" />
                    <label className="text-xs text-gray-500">{tf({ fr: "Date d'encaissement", ar: "تاريخ الصرف" })}
                      <input type="date" value={chq.date} onChange={(e) => setChq({ ...chq, date: e.target.value })} data-testid="cheque-date" className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-orange" /></label>
                  </div>
                )}
              </div>

              <button onClick={checkout} disabled={!pay} data-testid="cart-checkout-btn" className="w-full btn-navy font-display font-semibold rounded-full py-3 mt-3 disabled:opacity-50 disabled:cursor-not-allowed">{t("cart_checkout")}</button>
              <button onClick={() => { setCartOpen(false); navigate("/catalogue"); }} className="w-full text-navy font-display font-semibold text-sm py-1 hover:text-orange">{t("cart_continue")}</button>
            </div>
          </>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
